import React, { FC, useCallback, useEffect, useState } from 'react';
import { LegacyEmployee } from 'src/interfaces/api/Employee';
import EmployeeConcernedCard from './EmployeeConcernedCard';
import { LegacyAppointment } from 'src/interfaces/api/Collaborative';
import { useSetRecoilState } from 'recoil';
import { appointmentState } from 'src/recoil/selectors/appointments';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  appointmentId: LegacyAppointment['id'];
  employeeIds: LegacyEmployee['id'][];
}

const EmployeeConcernedCardContainer: FC<Props> = ({ appointmentId, employeeIds }) => {
  const setAppointment = useSetRecoilState(appointmentState(appointmentId));

  const [employees, setEmployees] = useState<LegacyEmployee[]>([]);
  const [, setLoading] = useState(false);
  const [, setError] = useState<string>();
  const { fetch } = useAuth();

  const updateEmployeesConcerned = (
    employeeList: LegacyEmployee['id'][],
    fieldName: 'employeesToRemove' | 'employeesToAdd'
  ): Promise<LegacyEmployee[]> => {
    const employeesIds = employeeList.map((employeeId) => ({ id: employeeId }));
    const graphQL = `mutation { appointment(id:${appointmentId}, ${fieldName}:${JSON.stringify(
      employeesIds
    )}){ employeesConcerned{ id }}}`;

    const data = 'data=' + encodeURIComponent(graphQL);

    return fetch('/api/appointment/show/', {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      method: 'POST',
      body: data,
    })
      .then((resp) => resp.json())
      .then((resp) => {
        const employeesConcerned = resp?.data?.appointment?.employeesConcerned || undefined;

        if (employeesConcerned) {
          setAppointment((appointment) => {
            const newAppointment = { ...appointment };

            newAppointment.employeesIds = employeesConcerned.map((employee) => employee.id);

            return newAppointment;
          });

          return employeesConcerned;
        }

        throw new Error('Erreur lors de la modification du rendez-vous');
      });
  };

  const fetchEmployees = (): Promise<LegacyEmployee[]> => {
    const queryURL = '/api/employee/';
    const graphQL = '{ employees(all: true){ id, firstname, lastname, profileImage, user{ id, email }}}';

    return fetch(queryURL + graphQL, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'GET',
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data['data']['employees']) {
          return data['data']['employees'];
        }

        throw new Error('Aucun employé trouvé ...');
      });
  };

  useEffect(() => {
    fetchEmployees()
      .then((employees) => {
        setEmployees(employees);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const getEmployeesConcerned = useCallback(() => {
    return employees.filter((employee) => employeeIds.includes(employee.id));
  }, [employeeIds, employees]);

  return <EmployeeConcernedCard onUpdateEmployees={updateEmployeesConcerned} employees={getEmployeesConcerned()} />;
};

export default React.memo(
  EmployeeConcernedCardContainer,
  (prevProps, nextProps) => prevProps.employeeIds === nextProps.employeeIds
);
